/**
 * The message protocol between the embedded configurator and the host page.
 *
 * The embed runs in an iframe on a website we do not control, so every
 * message crosses an origin boundary. Two rules hold on both sides and
 * are the reason this file exists rather than inline `postMessage`
 * calls:
 *
 * 1. **Everything is namespaced.** A host page may run analytics,
 *    chat widgets and a cookie banner that all speak `postMessage`;
 *    an unprefixed `{type: "resize"}` would be ambiguous to everyone.
 * 2. **The host verifies the sender, we never verify the host.** The
 *    loader checks `event.origin` against the iframe's own origin
 *    (`widget.js`). We deliberately do NOT check who the host is —
 *    the allow-list of who may frame us is enforced by the browser
 *    from `frame-ancestors` (em03), which is a real boundary. A
 *    JavaScript check here would only look like one.
 */

import { CLAIM_PARAM, currentGuestHandle } from "@/embed/guestSession";
import { apiFetch } from "@/lib/api";

/** Prefix on every message, in both directions. */
export const EMBED_MESSAGE_PREFIX = "forgmatic:embed:";

export const EMBED_READY = `${EMBED_MESSAGE_PREFIX}ready` as const;
export const EMBED_RESIZE = `${EMBED_MESSAGE_PREFIX}resize` as const;
export const EMBED_NAVIGATE = `${EMBED_MESSAGE_PREFIX}navigate` as const;

export interface EmbedReadyMessage {
  type: typeof EMBED_READY;
}

export interface EmbedResizeMessage {
  type: typeof EMBED_RESIZE;
  /** Document height in CSS pixels. */
  height: number;
}

export interface EmbedNavigateMessage {
  type: typeof EMBED_NAVIGATE;
  /** Absolute URL on the embed's OWN origin. The loader re-checks. */
  url: string;
}

export type EmbedMessage =
  | EmbedReadyMessage
  | EmbedResizeMessage
  | EmbedNavigateMessage;

/** True when this document is running inside a frame. */
export function isFramed(): boolean {
  try {
    return window.parent !== window;
  } catch {
    // Cross-origin parent access threw — which itself means framed.
    return true;
  }
}

function post(message: EmbedMessage): void {
  // ``*`` as target origin: we do not know which of the tenant's
  // allowed sites we are on, and none of these messages carry
  // anything private — a height and a URL that is already public.
  // Narrowing it would mean shipping the allow-list into the page.
  // nosemgrep: javascript.browser.security.wildcard-postmessage-configuration.wildcard-postmessage-configuration
  window.parent.postMessage(message, "*");
}

/** Tell the host the configurator has mounted. */
export function announceReady(): void {
  if (isFramed()) post({ type: EMBED_READY });
}

/** Tell the host how tall the iframe should be. */
export function announceHeight(height: number): void {
  if (isFramed()) post({ type: EMBED_RESIZE, height });
}

/**
 * Leave the iframe for ``path`` on the tenant's own storefront.
 *
 * Signature matches ``useNavigate``'s string form so it can stand in
 * for it directly at the call site.
 *
 * Falls back to a same-tab navigation when not framed (dev, direct
 * visit to ``/embed``), so the flow never dead-ends.
 */
export async function requestEmbedBreakout(path: string): Promise<void> {
  const url = new URL(path, window.location.origin);

  // Carry the cart across. The storefront cannot read the embed's
  // localStorage (different top-level site → partitioned store), and
  // the cookie the shop would normally use was never set inside the
  // frame. Without this the visitor lands on the shop with an empty
  // cart and the upload they just made is gone.
  //
  // In the FRAGMENT, not the query (KAN-506). This is a document
  // navigation: a query string is part of the request line and lands
  // verbatim in every access log on the way — Vercel's edge, the CDN,
  // any reverse proxy. A fragment is never sent to a server at all.
  //
  // As a one-time TOKEN, not the handle (KAN-1258). The handle is the
  // cart's capability (the uploaded drawing, the cart contents, and
  // after checkout the delivery address); a link that installs it in
  // whoever opens it is a session-fixation attack. The token lives a
  // minute, redeems once, and redeeming it rotates the cart's key — so
  // the copy left in this frame's storage stops keying anything.
  if (currentGuestHandle()) {
    try {
      const { token } = await apiFetch<{ token: string }>("/api/v1/guest-session/handoff", {
        method: "POST",
      });
      url.hash = `${CLAIM_PARAM}=${encodeURIComponent(token)}`;
    } catch {
      // No token, no claim. A handle with no cart behind it (404) has
      // nothing to carry; anything else means the visitor lands with an
      // empty cart — worse than before, but never the handle in a URL.
    }
  }

  if (!isFramed()) {
    window.location.href = url.toString();
    return;
  }
  post({ type: EMBED_NAVIGATE, url: url.toString() });
}
